import { RENDER_PALETTE } from "./render-palette";

interface DiamondBurst {
  centerX: number;
  centerY: number;
  initialSize: number;
  color: number;
  alpha: number;
  elapsedMs: number;
  durationMs: number;
  dead: boolean;
}

const DIAMOND_EXPAND_PX = 48;
const DIAMOND_LINE_WIDTH = 2;
const DIAMOND_DURATION_MS = 500;

export class BreadcrumbEffectRenderer {
  private readonly graphics: Phaser.GameObjects.Graphics;
  private readonly bursts: DiamondBurst[] = [];

  constructor(scene: Phaser.Scene) {
    this.graphics = scene.add.graphics();
    this.graphics.setDepth(2);
  }

  public spawn(pixelX: number, pixelY: number, size: number, color: number): void {
    const centerX = pixelX + size / 2;
    const centerY = pixelY + size / 2;
    this.bursts.push({
      centerX,
      centerY,
      initialSize: size,
      color,
      alpha: 1.0,
      elapsedMs: 0,
      durationMs: DIAMOND_DURATION_MS,
      dead: false,
    });
  }

  public update(delta: number): void {
    for (let i = 0; i < this.bursts.length; i += 1) {
      const burst = this.bursts[i];
      if (burst === undefined) continue;

      burst.elapsedMs += delta;
      const progress = Math.min(burst.elapsedMs / burst.durationMs, 1);
      burst.alpha = 1 - progress;

      if (burst.elapsedMs >= burst.durationMs) {
        burst.dead = true;
      }
    }

    for (let i = this.bursts.length - 1; i >= 0; i -= 1) {
      if (this.bursts[i]?.dead === true) {
        this.bursts.splice(i, 1);
      }
    }
  }

  public render(): void {
    this.graphics.clear();

    for (const burst of this.bursts) {
      const progress = Math.min(burst.elapsedMs / burst.durationMs, 1);
      const expandedHalf = (burst.initialSize / 2) + progress * (DIAMOND_EXPAND_PX / 2);

      this.graphics.lineStyle(DIAMOND_LINE_WIDTH, burst.color, burst.alpha);
      this.graphics.strokePoints(
        [
          { x: burst.centerX, y: burst.centerY - expandedHalf },
          { x: burst.centerX + expandedHalf, y: burst.centerY },
          { x: burst.centerX, y: burst.centerY + expandedHalf },
          { x: burst.centerX - expandedHalf, y: burst.centerY },
        ],
        true,
        true,
      );
    }
  }

  public destroy(): void {
    this.graphics.destroy();
  }
}
